import React, { useCallback, useState } from "react";
import { useDropzone, FileRejection, Accept } from "react-dropzone";
import JSZip from "jszip";
import SmallSpinner from "../loading/SmallSpinner";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "../buttons/SecondaryButton";
import CheckBox from "../selection/CheckBox";

interface DropzoneProps {
  onDrop?: (files: File[]) => void;
  files?: File[];
  maxFiles?: number;
  maxSize?: number;
  dropzoneStyles?: string;
  instructionsText?: string;
  onDelete?: (file: File) => void;
  loading?: boolean;
  mandatory?: boolean;
  only_zip?: boolean;
  handleClickVariant?: (message: string, variant: "error" | "success") => void;
  selected_files?: File[];
  setSelectedDroppedFiles?: (files: File[]) => void;
  title?: string;
  showImagePreview?: boolean;
  acceptTypes?: Record<string, string[]>;
}

const Dropzone: React.FC<DropzoneProps> = ({
  onDrop,
  files = [],
  maxFiles = Infinity,
  maxSize = 52428800,
  dropzoneStyles = "",
  onDelete,
  loading,
  mandatory = false,
  only_zip = false,
  handleClickVariant,
  selected_files,
  setSelectedDroppedFiles,
  title,
  showImagePreview = false,
  acceptTypes,
}) => {
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [onHover, setOnHover] = useState<boolean>(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      setLoadingData(true);

      const allowedTypes = [
        "text/xml",
        "application/x-zip-compressed",
        "application/zip",
        "application/xml",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ];

      let newFiles: File[] = [];
      for (const file of acceptedFiles) {
        if (!allowedTypes.includes(file.type)) {
          if (only_zip) {
            console.error(file.type);
            handleClickVariant?.(
              "Veuillez déposer un fichier au bon format",
              "error",
            );
            newFiles = [];
            break;
          } else {
            newFiles.push(file);
          }
        } else {
          newFiles.push(file);
        }
      }

      if (newFiles.length > 0) {
        onDrop && onDrop([...files, ...newFiles]);
      }

      setLoadingData(false);
    },
    [onDrop, files, only_zip, handleClickVariant],
  );

  const onDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      rejectedFiles.forEach((file) => {
        if (file.file.size > maxSize) {
          console.error(
            `Rejected file due to size: ${file.file.name}. Size: ${file.file.size} bytes. Max allowed size: ${maxSize} bytes.`,
          );
          handleClickVariant?.(
            `Fichier rejeté : ${file.file.name}. Taille maximale autorisée : ${maxSize / 1000000} Mo.`,
            "error",
          );
        } else {
          console.warn(
            `Rejected file: ${file.file.name}, Type: ${file.file.type}`,
          );
        }
      });
      const fileNames = rejectedFiles.map((file) => file.file.name);
      alert(`Invalid file types or sizes: ${fileNames.join(", ")}`);
    },
    [maxSize, handleClickVariant],
  );

  const acceptedFileTypes: Accept = {
    "application/pdf": [".pdf"],
    "application/xml": [".xml"],
    "application/zip": [".zip"],
    "application/x-zip-compressed": [".zip"],
    "text/xml": [".xml"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    onDropRejected,
    accept: acceptTypes || acceptedFileTypes,
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div className="flex flex-col items-center w-full ">
      <div
        {...getRootProps()}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
        className={`  w-full h-[200px] flex flex-col justify-center items-center cursor-pointer transition-colors ${
          mandatory
            ? " border-red-500 bg-red-50"
            : isDragActive
              ? "bg-neutral-100 border-2 border-neutral-500"
              : onHover
                ? "bg-neutral-200 border-2 border-neutral-500"
                : "bg-neutral-100"
        } ${dropzoneStyles}`}
      >
        <input {...getInputProps()} />
        {loadingData ? (
          <SmallSpinner loading={true} />
        ) : (
          <div className="flex flex-col items-center">
            {isDragActive ? (
              <div className="flex flex-col items-center gap-4">
                <Icon1 name="join" size={26} />
                <div className="text-h2 font-semibold">Lâchez pour déposer</div>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center gap-4">
                <Icon1 name="file" size={26} />
                <div className="text-h2 font-semibold">
                  Téléchargement en cours...
                </div>
                <SmallSpinner loading={true} />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <Icon1 name="upload" size={26} />
                <div className="text-h2 text-center px-4">
                  {title || "Choisir un fichier ou glissez le ici."}
                </div>
                <SecondaryButton>Parcourir les fichiers</SecondaryButton>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Display the dropped files */}
      {files.length !== 0 && showImagePreview && (
        <div className="w-full border-t-1 p-4 overflow-y-auto max-h-[300px]">
          <div className="flex flex-wrap gap-3">
            {files.map((file, index) => (
              <div key={index} className="relative group">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-28 h-28 object-cover rounded-xl border"
                />
                <div
                  className="absolute top-1 right-1 bg-white rounded-full p-1 shadow cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onDelete && onDelete(file)}
                >
                  <Icon1 name="delete" size={16} />
                </div>
                <div className="text-h5 text-neutral-400 mt-1 max-w-28 truncate">
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {files.length !== 0 && !showImagePreview && (
        <div className="w-full rounded-lg flex flex-col border-t-1 h-fit p-4 gap-1 overflow-y-auto max-h-[300px]">
          {files.map((file, index) => (
            <div
              key={index}
              className="w-full flex flex-row justify-start items-center"
            >
              <div className="flex flex-row gap-2 items-center mr-auto">
                {selected_files && setSelectedDroppedFiles && (
                  <CheckBox
                    checked={selected_files.includes(file)}
                    onChange={() => {
                      if (selected_files.includes(file)) {
                        setSelectedDroppedFiles(
                          selected_files.filter((f) => f !== file),
                        );
                      } else {
                        setSelectedDroppedFiles([...selected_files, file]);
                      }
                    }}
                  />
                )}

                <Icon1
                  name={
                    file.name.endsWith(".jpg") ||
                    file.name.endsWith(".jpeg") ||
                    file.name.endsWith(".png")
                      ? "image"
                      : file.name.endsWith(".xml")
                        ? "xml"
                        : file.name.endsWith(".zip") ||
                            file.name.endsWith(".rar")
                          ? "folder"
                          : "file"
                  }
                  size={20}
                />
                <div className="flex flex-col">
                  <div className="text-h3 text-black">{file.name}</div>
                </div>
              </div>
              {(file.name.endsWith(".jpg") ||
                file.name.endsWith(".jpeg") ||
                file.name.endsWith(".png")) && (
                <div className="mr-2">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-auto h-10 object-cover rounded-md"
                  />
                </div>
              )}
              <div
                className="cursor-pointer"
                onClick={() => onDelete && onDelete(file)}
              >
                <Icon1 name="delete" size={18} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropzone;

export const processZipFile = async (zipFile: File): Promise<File | null> => {
  const zip = new JSZip();
  const content = await zip.loadAsync(zipFile);
  const xmlFile = Object.keys(content.files).find((fileName) =>
    fileName.endsWith(".xml"),
  );

  if (xmlFile) {
    const xmlContent = await zip.file(xmlFile)?.async("string");
    if (xmlContent) {
      const blob = new Blob([xmlContent], { type: "application/xml" });
      const extractedFile = new File([blob], xmlFile, {
        type: "application/xml",
      });
      return extractedFile;
    }
  }

  return null;
};
